import React, { useEffect, useState } from "react";
import { Rate, Row, Col, Avatar, Skeleton } from "antd";
import ProductFeedbackServ from "../service/ProductFeedbackServ";
import { IconUser, IconQuote, IconShieldCheck } from "@tabler/icons-react";
import GlobalTooltip from "./GlobalTooltip";
import { renderSafeText } from "../util/unitFormatter";
import "../css/TopFeedback.css";

const TopFeedback = ({ latitude, longitude, radius }) => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTopFeedbacks();
    }, [latitude, longitude, radius]);

    const fetchTopFeedbacks = async () => {
        try {
            setLoading(true);
            const params = {
                limit: 4,
                sort: JSON.stringify({ rating: -1, createdAt: -1 })
            };
            
            if (latitude && longitude) {
                params.latitude = latitude;
                params.longitude = longitude;
                if (radius) params.radius = radius;
            }

            const res = await ProductFeedbackServ.index(params);
            setFeedbacks(res.data || []);
        } catch (err) {
            console.error("Error fetching top feedbacks:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (productId) => {
        if (productId) {
            window.open(`/single/Products/${productId}`, '_blank');
        }
    };

    const avatarColors = [
        'linear-gradient(135deg, #6c3fcc, #9b6dff)',
        'linear-gradient(135deg, #0f7ac0, #38b2f0)',
        'linear-gradient(135deg, #b83280, #f687b3)',
        'linear-gradient(135deg, #276749, #68d391)',
    ];

    if (!loading && feedbacks.length === 0) {
        return null;
    }

    return (
        <section style={styles.section}>
            {/* Decorative blobs */}
            <div style={styles.blobLeft} />
            <div style={styles.blobRight} />

            <div style={styles.inner}>
                {/* Header */}
                <div style={styles.headerWrap}>
                    <span style={styles.badge}>⭐ CUSTOMER REVIEWS</span>
                    <h2 style={styles.heading}>What Our Customers Are Saying</h2>
                    <p style={styles.subheading}>Top reviews from our satisfied customers</p>
                </div>

                {/* Cards */}
                {loading ? (
                    <Row gutter={[24, 24]}>
                        {[1, 2, 3, 4].map(i => (
                            <Col xs={12} sm={12} md={12} lg={6} key={i}>
                                <div className="top-feedback-card">
                                    <Skeleton avatar active paragraph={{ rows: 3 }} />
                                </div>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Row gutter={[24, 24]}>
                        {feedbacks.map((f, idx) => (
                            <Col xs={12} sm={12} md={12} lg={6} key={f._id}>
                                <div
                                    className="top-feedback-card"
                                    style={{
                                        cursor: f.product?._id ? 'pointer' : 'default',
                                    }}
                                    onClick={() => handleCardClick(f.product?._id)}
                                >
                                    {/* Quote Icon */}
                                    <div style={styles.quoteIcon}>
                                        <IconQuote size={28} style={{ color: 'rgba(155, 109, 255, 0.6)' }} />
                                    </div>

                                    {/* User Info */}
                                    <div className="top-feedback-user-row">
                                        <div className="top-feedback-avatar" style={{ background: avatarColors[idx % avatarColors.length] }}>
                                            <IconUser size={20} color="#fff" />
                                        </div>
                                        <div>
                                            <div className="top-feedback-user-name">
                                                {renderSafeText(f.user?.firstName || "Customer")}{" "}
                                                {renderSafeText(f.user?.lastName || "")}
                                            </div>
                                            <div className="top-feedback-verified">
                                                <IconShieldCheck size={12} color="#68d391" />
                                                <span>Verified Buyer</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stars */}
                                    <div className="top-feedback-stars" style={{ marginBottom: 14 }}>
                                        <Rate disabled defaultValue={f.rating} style={{ fontSize: 14, color: '#f6c90e' }} />
                                    </div>

                                    {/* Comment */}
                                    <GlobalTooltip title={f.comment} placement="topLeft">
                                        <p className="top-feedback-comment">
                                            "{renderSafeText(f.comment)}"
                                        </p>
                                    </GlobalTooltip>

                                    {/* Product Tag */}
                                    {f.product && (
                                        <div className="top-feedback-product-tag">
                                            <span className="top-feedback-product-label">Product</span>
                                            <span className="top-feedback-product-name">
                                                {renderSafeText(f.product.name)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        </section>
    );
};

const styles = {
    section: {
        position: 'relative',
        background: 'linear-gradient(135deg, #f3f0ff 0%, #ede8ff 50%, #f0eeff 100%)',
        padding: '72px 0',
        overflow: 'hidden',
    },
    blobLeft: {
        position: 'absolute',
        top: '-80px',
        left: '-100px',
        width: '350px',
        height: '350px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(108,63,204,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    blobRight: {
        position: 'absolute',
        bottom: '-80px',
        right: '-100px',
        width: '350px',
        height: '350px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(155,109,255,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    inner: {
        position: 'relative',
        width: '80%',
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    headerWrap: {
        textAlign: 'center',
        marginBottom: '48px',
    },
    badge: {
        display: 'inline-block',
        padding: '5px 16px',
        borderRadius: '50px',
        background: 'rgba(108,63,204,0.25)',
        border: '1px solid rgba(155,109,255,0.4)',
        color: 'black',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        marginBottom: '16px',
    },
    heading: {
        color: '#1a1035',
        fontSize: '32px',
        fontWeight: 800,
        margin: '0 0 10px 0',
        letterSpacing: '-0.5px',
    },
    subheading: {
        color: '#6b6b8a',
        fontSize: '15px',
        margin: 0,
    },
    skeletonCard: {
        background: '#ffffff',
        border: '1px solid rgba(108,63,204,0.12)',
        borderRadius: '18px',
        padding: '24px',
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 24px',
        background: '#ffffff',
        borderRadius: '18px',
        border: '1px dashed rgba(108,63,204,0.25)',
    },
};

export default TopFeedback;
